import Vue from "vue"
import { MutationTree, ActionTree, GetterTree } from "vuex"

import Tag from "~/entities/tag"
import TagType from "~/entities/tagType"
import TagParent from "~/entities/tagParent"
import resetMixin from "~/mixins/store/resetToInitialState"

import { RootState } from "~/store"

const initialState = () => ({
  parents: [] as TagParent[],
  tags: [] as Tag[],
  types: [] as TagType[],
})
export const state = initialState
export type TagState = ReturnType<typeof state>
type AnyTagType = typeof TagType | typeof TagParent | typeof Tag
interface MixedRawTagData {
  id: number
  parent: number | null
  name: string
  parent_id?: number
  parent_ids?: number[]
  tag_ids?: number[]
}
type ApiFetchTagResponse = Record<keyof TagState, MixedRawTagData[]>

export const mutations: MutationTree<TagState> = {
  SET(state, patch: ApiFetchTagResponse) {
    const configBuldle: { key: keyof TagState; entity: AnyTagType }[] = [
      {
        key: "tags",
        entity: Tag,
      },
      {
        key: "parents",
        entity: TagParent,
      },
      {
        key: "types",
        entity: TagType,
      },
    ]

    configBuldle.forEach(
      (config: { key: keyof TagState; entity: AnyTagType }) => {
        const data = patch[config.key]
          .filter(
            (patchEntity) =>
              !state[config.key].some(
                ({ id }: { id: number }) => patchEntity.id === id
              )
          )
          .map((rawData: any) => new config.entity(rawData))

        Vue.set(state, config.key, [...state[config.key], ...data])
      }
    )
  },
  ...resetMixin.mutations,
}

export const actions: ActionTree<TagState, RootState> = {
  FETCH({ commit, state }): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!state.types.length || !state.parents.length || !state.tags.length) {
        this.$axios
          .$get("/tag/meta/")
          .then((data: ApiFetchTagResponse) => {
            commit("SET", data)
            resolve()
          })
          .catch((err) => {
            reject(err)
          })
      } else resolve()
    })
  },
}

export const getters: GetterTree<TagState, RootState> = {
  GET_PARENT_BY_IDS(state) {
    return function (parentIds: number[]): TagParent[] {
      return state.parents.filter((parent) => parentIds.includes(parent.id))
    }
  },
  GET_TAGS_FROM_PARENT_ID(state) {
    return function (parentId: number): Tag[] {
      return state.tags.filter((tag) => tag.parent_id === parentId)
    }
  },
  GET_PARENT_BY_TAGS(_, getters) {
    return function (tagArray: Tag[]): TagParent[] {
      return getters.GET_PARENT_BY_IDS(tagArray.map((tag: Tag) => tag.id))
    }
  },
  GET_TAG_FROM_ID(state) {
    return function (tagId: number): Tag | undefined {
      return state.tags.find((tag: Tag) => tag.id === tagId)
    }
  },
  GET_TAGS_FROM_IDS(state) {
    return function (tagIds: number[]): Tag[] {
      return state.tags.filter((tag: Tag) => tagIds.includes(tag.id))
    }
  },
  GET_TYPES_FROM_TAG_IDS(_, getters) {
    return function (tagIds: number[]): TagType[] {
      return tagIds.reduce((accumulator: TagType[], tag_id) => {
        const type: TagType = getters.GET_TYPE_FROM_TAG_ID(tag_id)

        if (!accumulator.find((tagType) => tagType.id === type.id))
          accumulator.push(type)
        return accumulator
      }, [] as TagType[])
    }
  },
  GET_TYPE_FROM_TAG_ID(state) {
    return function (tagId: number): TagType | undefined {
      return state.types.find((tagType) => tagType.tag_ids.includes(tagId))
    }
  },
  GET_TYPE_FROM_NAME(state) {
    return function (typeName: string): TagType | undefined {
      return state.types.find((type) => type.name === typeName)
    }
  },
  GET_TAGS_FROM_NAMES(state) {
    return function (tagNames: string): Tag[] {
      return state.tags.filter((tag) => tagNames.includes(tag.name))
    }
  },
}
